import { Injectable } from '@angular/core';
import { DetachedRouteHandle } from '@angular/router';

/**
 * Application-wide store of detached Angular route handles, keyed by normalized
 * tab URL (see `normalizeTabRouteKey`). `TabRouteReuseStrategy` reads/writes here,
 * and `TabService` deletes an entry when its tab is closed so the next visit to
 * that URL loads a fresh component tree. Provided in root so the single instance
 * is shared between the reuse strategy and the tab service.
 */
@Injectable({ providedIn: 'root' })
export class TabRouteCacheRegistry {
    private readonly handles = new Map<string, DetachedRouteHandle>();

    /** Whether a handle is stored for the given tab URL key. */
    has(key: string): boolean {
        return this.handles.has(key);
    }

    /** Returns the stored handle for the key, or `undefined` if none. */
    get(key: string): DetachedRouteHandle | undefined {
        return this.handles.get(key);
    }

    /** Parks a detached component handle under the given tab URL key. */
    set(key: string, handle: DetachedRouteHandle): void {
        this.handles.set(key, handle);
    }

    /** Drops the handle for a key, e.g. when its tab is closed. */
    delete(key: string): void {
        this.handles.delete(key);
    }

    /** Clears every cached handle (used on full tab reset / logout). */
    clearAll(): void {
        this.handles.clear();
    }
}
